import {
  CallData,
  ec,
  encode,
  hash,
  shortString,
  stark,
  type Call,
} from "starknet";
import { SessionProtocolError } from "./errors";
import type { PolicyMerkleProof } from "./merkle";
import {
  normalizeFelt,
  normalizeContractAddress,
  selectorFromEntrypoint,
} from "./utils";

const ZERO_FELT = "0x0";
const ONE_FELT = "0x1";
const TWO_FELT = "0x2";

function shortFelt(value: string): string {
  return normalizeFelt(shortString.encodeShortString(value));
}

function selectorFelt(value: string): string {
  return normalizeFelt(hash.getSelectorFromName(value));
}

const STARKNET_MESSAGE = shortFelt("StarkNet Message");
const OUTSIDE_EXECUTION_CALLER_ANY = shortFelt("ANY_CALLER");
const SESSION_TOKEN_MAGIC = shortFelt("session-token");
const AUTHORIZATION_BY_REGISTERED = shortFelt("authorization-by-registered");

// Placeholder guardian key — Cartridge's server replaces this with the real
// guardian signature before submitting. Not a secret.
const GUARDIAN_KEY_PLACEHOLDER = shortFelt("CARTRIDGE_GUARDIAN");

// SNIP-12 type hashes
const STARKNET_DOMAIN_TYPE_HASH = selectorFelt(
  '"StarknetDomain"("name":"shortstring","version":"shortstring","chainId":"shortstring","revision":"shortstring")',
);
const CALL_TYPE_HASH = selectorFelt(
  '"Call"("To":"ContractAddress","Selector":"selector","Calldata":"felt*")',
);
const OUTSIDE_EXECUTION_TYPE_HASH = selectorFelt(
  '"OutsideExecution"("Caller":"ContractAddress","Nonce":"(felt,u128)","Execute After":"u128","Execute Before":"u128","Calls":"Call*")"Call"("To":"ContractAddress","Selector":"selector","Calldata":"felt*")',
);
const SESSION_TYPE_HASH = selectorFelt(
  '"Session"("Expires At":"timestamp","Allowed Methods":"merkletree","Metadata":"string","Session Key":"felt")',
);

const OUTSIDE_EXECUTION_DOMAIN_NAME = shortFelt("Account.execute_from_outside");
const SESSION_DOMAIN_NAME = shortFelt("SessionAccount.session");
const SESSION_DOMAIN_VERSION = shortFelt("1");

interface StarknetSignerSignature {
  pubkey: string;
  r: string;
  s: string;
}

export interface RpcOutsideExecutionCall {
  to: string;
  selector: string;
  calldata: string[];
}

export interface RpcOutsideExecutionV3 {
  caller: string;
  nonce: [string, string];
  execute_after: string;
  execute_before: string;
  calls: RpcOutsideExecutionCall[];
}

export interface SignedOutsideExecutionV3 {
  outsideExecution: RpcOutsideExecutionV3;
  signature: string[];
}

interface SessionStruct {
  expiresAt: string;
  allowedPoliciesRoot: string;
  metadataHash: string;
  sessionKeyGuid: string;
  guardianKeyGuid: string;
}

export interface SessionRegistration {
  username: string;
  address: string;
  ownerGuid: string;
  expiresAt: string;
  guardianKeyGuid: string;
  metadataHash: string;
  sessionKeyGuid: string;
}

export interface TimeBounds {
  executeAfter?: number | bigint;
  executeBefore?: number | bigint;
}

export interface BuildSignedOutsideExecutionV3Args {
  calls: Call[];
  timeBounds?: TimeBounds;
  chainId: string;
  session: SessionRegistration;
  sessionPrivateKey: string;
  policyRoot: string;
  sessionKeyGuid: string;
  policyProofIndex: ReadonlyMap<string, string[]>;
  nowSeconds?: number;
}

function toUintBigInt(value: string | number | bigint): bigint {
  if (typeof value === "bigint") return value;
  if (typeof value === "number") return BigInt(value);
  return BigInt(value.trim());
}

function feltFromValue(value: string | number | bigint): string {
  return normalizeFelt(toUintBigInt(value));
}

function normalizeChainId(chainId: string): string {
  const trimmed = chainId.trim();
  if (/^0x[0-9a-f]+$/i.test(trimmed)) {
    return normalizeFelt(trimmed);
  }
  return shortFelt(trimmed);
}

function normalizeExecutionCall(call: Call): {
  contractAddress: string;
  selector: string;
  calldata: string[];
} {
  const contractAddress = normalizeContractAddress(
    String((call as any).contractAddress ?? ""),
    "Outside execution call",
  );
  const entrypoint = String((call as any).entrypoint ?? "").trim();
  const selector = selectorFromEntrypoint(entrypoint);
  const calldata = CallData.toHex(call.calldata ?? []).map((f) =>
    normalizeFelt(f),
  );

  return { contractAddress, selector, calldata };
}

// --- Hashing ---

function hashCallStruct(call: RpcOutsideExecutionCall): string {
  const calldataHash = normalizeFelt(
    hash.computePoseidonHashOnElements(call.calldata),
  );
  return normalizeFelt(
    hash.computePoseidonHashOnElements([
      CALL_TYPE_HASH,
      call.to,
      call.selector,
      calldataHash,
    ]),
  );
}

function hashStarknetDomain(
  name: string,
  version: string,
  chainId: string,
  revision: string,
): string {
  return normalizeFelt(
    hash.computePoseidonHashOnElements([
      STARKNET_DOMAIN_TYPE_HASH,
      name,
      version,
      chainId,
      revision,
    ]),
  );
}

function hashMessageRev1(
  domainHash: string,
  contractAddress: string,
  structHash: string,
): string {
  return normalizeFelt(
    hash.computePoseidonHashOnElements([
      STARKNET_MESSAGE,
      domainHash,
      contractAddress,
      structHash,
    ]),
  );
}

function hashOutsideExecutionMessage(
  outsideExecution: RpcOutsideExecutionV3,
  chainId: string,
  contractAddress: string,
): string {
  const callHashes = outsideExecution.calls.map(hashCallStruct);
  const callHashesHash = normalizeFelt(
    hash.computePoseidonHashOnElements(callHashes),
  );

  const structHash = normalizeFelt(
    hash.computePoseidonHashOnElements([
      OUTSIDE_EXECUTION_TYPE_HASH,
      outsideExecution.caller,
      outsideExecution.nonce[0],
      outsideExecution.nonce[1],
      outsideExecution.execute_after,
      outsideExecution.execute_before,
      callHashesHash,
    ]),
  );

  const domainHash = hashStarknetDomain(
    OUTSIDE_EXECUTION_DOMAIN_NAME,
    TWO_FELT,
    chainId,
    TWO_FELT,
  );

  return hashMessageRev1(domainHash, contractAddress, structHash);
}

function hashSessionStruct(session: SessionStruct): string {
  return normalizeFelt(
    hash.computePoseidonHashOnElements([
      SESSION_TYPE_HASH,
      session.expiresAt,
      session.allowedPoliciesRoot,
      session.metadataHash,
      session.sessionKeyGuid,
      session.guardianKeyGuid,
    ]),
  );
}

function hashSessionMessage(
  session: SessionStruct,
  chainId: string,
  contractAddress: string,
): string {
  const domainHash = hashStarknetDomain(
    SESSION_DOMAIN_NAME,
    SESSION_DOMAIN_VERSION,
    chainId,
    ONE_FELT,
  );
  return hashMessageRev1(
    domainHash,
    contractAddress,
    hashSessionStruct(session),
  );
}

// --- Signing ---

function signStarknet(
  messageHash: string,
  privateKey: string,
): StarknetSignerSignature {
  const normalizedKey = encode.addHexPrefix(privateKey.trim());
  const signature = ec.starkCurve.sign(messageHash, normalizedKey);
  return {
    pubkey: normalizeFelt(ec.starkCurve.getStarkKey(normalizedKey)),
    r: normalizeFelt(signature.r),
    s: normalizeFelt(signature.s),
  };
}

// --- Serialization ---

function serializeArray(values: readonly string[]): string[] {
  return [feltFromValue(values.length), ...values.map((v) => normalizeFelt(v))];
}

function serializeArrayOfArrays(values: readonly string[][]): string[] {
  const out: string[] = [feltFromValue(values.length)];
  for (const value of values) {
    out.push(...serializeArray(value));
  }
  return out;
}

function serializeSessionStruct(session: SessionStruct): string[] {
  return [
    session.expiresAt,
    session.allowedPoliciesRoot,
    session.metadataHash,
    session.sessionKeyGuid,
    session.guardianKeyGuid,
  ];
}

function serializeStarknetSignerSignature(
  sig: StarknetSignerSignature,
): string[] {
  // SignerSignature variant 0 = Starknet, followed by (pubkey, r, s)
  return [ZERO_FELT, sig.pubkey, sig.r, sig.s];
}

function serializeSessionToken(args: {
  session: SessionStruct;
  sessionAuthorization: string[];
  sessionSignature: StarknetSignerSignature;
  guardianSignature: StarknetSignerSignature;
  proofs: string[][];
}): string[] {
  return [
    ...serializeSessionStruct(args.session),
    ONE_FELT, // Variant discriminator for "registered session" token format
    ...serializeArray(args.sessionAuthorization),
    ...serializeStarknetSignerSignature(args.sessionSignature),
    ...serializeStarknetSignerSignature(args.guardianSignature),
    ...serializeArrayOfArrays(args.proofs),
  ];
}

// --- Policy proof index ---

export function createPolicyProofIndex(
  proofs: readonly PolicyMerkleProof[],
): Map<string, string[]> {
  const index = new Map<string, string[]>();
  for (const proof of proofs) {
    const key = `${normalizeContractAddress(proof.contractAddress, "Policy proof")}:${normalizeFelt(proof.selector)}`;
    if (!index.has(key)) {
      index.set(
        key,
        proof.proof.map((v) => normalizeFelt(v)),
      );
    }
  }
  return index;
}

function resolveCallProofs(
  calls: { contractAddress: string; selector: string }[],
  policyProofIndex: ReadonlyMap<string, string[]>,
): string[][] {
  return calls.map((call) => {
    const key = `${call.contractAddress}:${call.selector}`;
    const proof = policyProofIndex.get(key);
    if (!proof) {
      throw new SessionProtocolError(
        `Call is not authorized by session policies: ${key}`,
      );
    }
    return proof;
  });
}

// --- Main entry point ---

export function buildSignedOutsideExecutionV3({
  calls,
  timeBounds,
  chainId,
  session,
  sessionPrivateKey,
  policyRoot,
  sessionKeyGuid,
  policyProofIndex,
  nowSeconds,
}: BuildSignedOutsideExecutionV3Args): SignedOutsideExecutionV3 {
  if (calls.length === 0) {
    throw new SessionProtocolError("At least one call is required.");
  }

  const normalizedCalls = calls.map(normalizeExecutionCall);
  const proofs = resolveCallProofs(normalizedCalls, policyProofIndex);

  const now = toUintBigInt(nowSeconds ?? Math.floor(Date.now() / 1000));
  const executeAfter = toUintBigInt(timeBounds?.executeAfter ?? 0);
  const executeBefore = toUintBigInt(timeBounds?.executeBefore ?? now + 600n);

  if (executeBefore <= executeAfter) {
    throw new SessionProtocolError(
      "Outside execution window is invalid: execute_before must be greater than execute_after.",
    );
  }

  const outsideExecution: RpcOutsideExecutionV3 = {
    caller: OUTSIDE_EXECUTION_CALLER_ANY,
    nonce: [normalizeFelt(stark.randomAddress()), ONE_FELT],
    execute_after: feltFromValue(executeAfter),
    execute_before: feltFromValue(executeBefore),
    calls: normalizedCalls.map((c) => ({
      to: c.contractAddress,
      selector: c.selector,
      calldata: c.calldata,
    })),
  };

  const sessionAddress = normalizeContractAddress(
    session.address,
    "Session address",
  );
  const feltChainId = normalizeChainId(chainId);

  const txHash = hashOutsideExecutionMessage(
    outsideExecution,
    feltChainId,
    sessionAddress,
  );

  const sessionStruct: SessionStruct = {
    expiresAt: feltFromValue(session.expiresAt),
    allowedPoliciesRoot: feltFromValue(policyRoot),
    metadataHash: feltFromValue(session.metadataHash ?? ZERO_FELT),
    sessionKeyGuid: feltFromValue(session.sessionKeyGuid || sessionKeyGuid),
    guardianKeyGuid: feltFromValue(session.guardianKeyGuid || ZERO_FELT),
  };

  const sessionHash = hashSessionMessage(
    sessionStruct,
    feltChainId,
    sessionAddress,
  );
  const sessionTokenHash = normalizeFelt(
    hash.computePoseidonHash(txHash, sessionHash),
  );

  const sessionSignature = signStarknet(sessionTokenHash, sessionPrivateKey);
  const guardianSignature = signStarknet(
    sessionTokenHash,
    GUARDIAN_KEY_PLACEHOLDER,
  );

  const sessionAuthorization = [
    AUTHORIZATION_BY_REGISTERED,
    feltFromValue(session.ownerGuid),
  ];

  const signature = [
    SESSION_TOKEN_MAGIC,
    ...serializeSessionToken({
      session: sessionStruct,
      sessionAuthorization,
      sessionSignature,
      guardianSignature,
      proofs,
    }),
  ];

  return { outsideExecution, signature };
}
